import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
//
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

export const TemaContext = createContext<{ modo: 'light' | 'dark' }>({ modo: 'light' });

/**
 * Paleta do new-sgm-v2, resolvida para hex.
 *
 * Os valores vêm de src/app/globals.css do SGM, onde são oklch combinados com
 * color-mix. Aqui ficam literais porque o MUI 5 não parseia oklch — alpha() e
 * darken() lançam erro em runtime ao receber esse formato. Os mesmos valores
 * estão em index.css como CSS vars, para o que é estilizado fora do MUI.
 */
const PALETA = {
  light: {
    background: '#f4f4f4',
    foreground: '#0a0a0a',
    card: '#f8f8f8',
    primary: '#171717',
    primaryForeground: '#fafafa',
    secondary: '#e7e7e7',
    muted: '#e4e4e4',
    mutedForeground: '#737373',
    border: '#c9c9c9',
    input: '#cecece',
    ring: '#a1a1a1',
    destructive: '#dc2626',
    success: '#16a34a',
    // bg-primary/90 do SGM resolvido sobre o --background: usar opacity no
    // botao desbotaria o texto junto, o que la nao acontece.
    primaryHover: '#2d2d2d',
  },
  dark: {
    background: '#0a0a0a',
    foreground: '#fafafa',
    card: '#171717',
    primary: '#e5e5e5',
    primaryForeground: '#171717',
    secondary: '#262626',
    muted: '#262626',
    mutedForeground: '#a1a1a1',
    border: 'rgba(255, 255, 255, 0.1)',
    input: 'rgba(255, 255, 255, 0.15)',
    ring: '#737373',
    destructive: '#ef4444',
    success: '#22c55e',
    primaryHover: '#cfcfcf',
  },
};

/** --radius: 0.625rem no SGM. Os aliases seguem a mesma escala do tema de lá. */
const RADIUS = 10;
const RADIUS_MD = RADIUS - 2; // botões e inputs (rounded-md)
const RADIUS_XL = RADIUS + 4; // card (rounded-xl)

const FONTE = [
  'Geist',
  'ui-sans-serif',
  'system-ui',
  '-apple-system',
  'Segoe UI',
  'Roboto',
  'Arial',
  'sans-serif',
].join(', ');

function criarTema(modo: 'light' | 'dark') {
  const c = PALETA[modo];

  return createTheme({
    palette: {
      mode: modo,
      // light/dark/contrastText explícitos: sem eles o MUI chama augmentColor
      // para derivar variações, e qualquer formato que ele não entenda vira
      // erro. Fixando aqui, nenhuma cor é calculada em runtime.
      primary: {
        main: c.primary,
        light: c.primary,
        dark: c.primary,
        contrastText: c.primaryForeground,
      },
      secondary: {
        main: c.secondary,
        light: c.secondary,
        dark: c.secondary,
        contrastText: c.foreground,
      },
      error: { main: c.destructive, contrastText: '#fafafa' },
      success: { main: c.success, contrastText: '#fafafa' },
      background: { default: c.background, paper: c.card },
      text: { primary: c.foreground, secondary: c.mutedForeground },
      divider: c.border,
    },

    shape: { borderRadius: RADIUS_MD },

    typography: {
      fontFamily: FONTE,
      // O SGM usa text-sm (14px) como corpo e pesos 500/600 nos títulos.
      h3: { fontSize: '1.875rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em' },
      subtitle1: { fontSize: '0.875rem', lineHeight: 1.5 },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: c.background, color: c.foreground },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: c.card,
            backgroundImage: 'none',
            borderRadius: RADIUS_XL,
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: RADIUS_MD,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
          },
          // h-9 / h-10 do SGM (36px e 40px).
          sizeMedium: { height: 36, paddingLeft: 16, paddingRight: 16 },
          sizeLarge: {
            height: 40,
            paddingLeft: 24,
            paddingRight: 24,
            fontSize: '0.875rem',
          },
          containedPrimary: {
            backgroundColor: c.primary,
            color: c.primaryForeground,
            '&:hover': {
              backgroundColor: c.primaryHover,
              boxShadow: 'none',
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS_MD,
            backgroundColor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.input },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.ring },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.ring,
              borderWidth: 1,
            },
          },
          input: { fontSize: '0.875rem' },
        },
      },

      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS_MD,
            backgroundColor: c.muted,
            '&:hover': { backgroundColor: c.muted },
            '&.Mui-focused': { backgroundColor: c.muted },
            '&:before, &:after': { display: 'none' },
          },
          input: { fontSize: '0.875rem' },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem', color: c.mutedForeground },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: c.border } } },

      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: c.muted, borderRadius: RADIUS_XL },
        },
      },
    },
  });
}

export default function TemaProvider({ children }: { children: ReactNode }) {
  /* O tema anterior lia prefers-color-scheme uma única vez, na montagem: quem
     trocasse o modo do sistema com a página aberta ficava com as cores do modo
     antigo. Aqui o listener mantém os dois em sincronia. */
  const [modo, setModo] = useState<'light' | 'dark'>(() =>
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = (e: MediaQueryListEvent) => setModo(e.matches ? 'dark' : 'light');

    // addEventListener não existe no Safari < 14; o fallback evita quebrar lá.
    if (mq.addEventListener) mq.addEventListener('change', aoMudar);
    else mq.addListener(aoMudar);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', aoMudar);
      else mq.removeListener(aoMudar);
    };
  }, []);

  /* A classe .dark no <html> é o que ativa os tokens do index.css — mesmo
     mecanismo do SGM, que alterna por classe e não por media query. */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', modo === 'dark');
    document.documentElement.style.colorScheme = modo;
  }, [modo]);

  const theme = useMemo(() => criarTema(modo), [modo]);
  const valor = useMemo(() => ({ modo }), [modo]);

  return (
    <TemaContext.Provider value={valor}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </TemaContext.Provider>
  );
}
